// The Subcontractor Portal access card on a subcontractor's page (Session ATA): the office adds a login, sends the
// sign-in code, disables or re-enables it, and removes it. The portal itself is read-only for the subcontractor (the jobs
// and hours of the engineers linked to them); nothing here changes what they are assigned.
//
// Every outward or consequential action confirms in two steps and names what it acts on (the branded confirm rule):
// sending a code names the address; disabling says the person is signed out at once; removal is the destructive
// ConfirmDeleteModal. A login holds no records of its own, so its removal is never blocked.
// Session AUV (design system Session 8): the loader and the sign-in time are the shared Loader and lib/format.ts
// (London time, "5 Oct 2026, 14:05", as before). Presentation only.
import { useCallback, useEffect, useState } from "react";
import { api, type SubcontractorPortalLogin } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Loader } from "../../components/Loader";
import { formatDateTime } from "../../lib/format";

const btnCls = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50";

export function SubcontractorPortalAccessSection({ subId, archived }: { subId: string; archived: boolean }) {
  const [logins, setLogins] = useState<SubcontractorPortalLogin[] | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState<SubcontractorPortalLogin | null>(null);

  const load = useCallback(() => {
    api.subcontractorPortalAdmin.list(subId).then(setLogins, (err) => setError(friendlyApiError(err, "Could not load the portal logins.")));
  }, [subId]);

  useEffect(() => {
    load();
  }, [load]);

  async function run(key: string, fn: () => Promise<string | null>) {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const done = await fn();
      if (done) setNotice(done);
      load();
    } catch (err) {
      setError(friendlyApiError(err, "That did not work. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  const add = () =>
    run("add", async () => {
      const created = await api.subcontractorPortalAdmin.create(subId, { email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}) });
      setEmail("");
      setName("");
      return `Login added for ${created.email}. Send the sign-in code when they are ready.`;
    });

  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h2 className="mb-3 font-heading text-lg uppercase tracking-wide text-ele-blue">Subcontractor Portal</h2>
      <p className="mb-3 max-w-2xl text-sm text-ele-body">
        A sign-in for this subcontractor to see, read-only, the jobs booked for the engineers linked to them and the hours
        recorded against those jobs. They cannot change a booking, accept a job, edit hours or raise an invoice there. They
        never see a customer's name or contact details, a site's access details, a price or their pay.
      </p>
      {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">{error}</p>}
      {notice && <p className="mb-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">{notice}</p>}

      {logins === null ? (
        <Loader label="Loading the portal logins..." />
      ) : logins.length === 0 ? (
        <p className="text-sm text-ele-body">No portal login yet.</p>
      ) : (
        <ul className="mb-4 space-y-2">
          {logins.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent px-3 py-2">
              <div>
                <p className="text-sm text-ele-strong">
                  {u.email}
                  {u.name ? <span className="text-ele-body"> ({u.name})</span> : null}
                </p>
                <p className="text-xs text-ele-body">
                  {u.disabledAt ? "Disabled" : "Active"} · {u.lastLoginAt ? `last signed in ${formatDateTime(u.lastLoginAt)}` : "never signed in"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {!u.disabledAt && !archived && (
                  <ConfirmButton
                    label="Send sign-in code"
                    confirmLabel={`Send a code to ${u.email}?`}
                    busy={busy === `code:${u.id}`}
                    busyLabel="Sending..."
                    armedClassName="!text-ele-strong font-semibold"
                    className={btnCls}
                    onConfirm={() =>
                      run(`code:${u.id}`, async () => {
                        const r = await api.subcontractorPortalAdmin.sendCode(subId, u.id);
                        return r.sent
                          ? `A sign-in code was emailed to ${u.email}.`
                          : `A code was issued but the email could not be sent (check Gmail is connected in Settings), then send it again.`;
                      })
                    }
                  />
                )}
                {u.disabledAt ? (
                  <button type="button" className={btnCls} disabled={busy !== null} onClick={() => run(`en:${u.id}`, async () => (await api.subcontractorPortalAdmin.setDisabled(subId, u.id, false), `${u.email} can sign in again with a new code.`))}>
                    Enable
                  </button>
                ) : (
                  <ConfirmButton
                    label="Disable"
                    confirmLabel="Sign them out now?"
                    busy={busy === `dis:${u.id}`}
                    busyLabel="Disabling..."
                    className={btnCls}
                    onConfirm={() => run(`dis:${u.id}`, async () => (await api.subcontractorPortalAdmin.setDisabled(subId, u.id, true), `${u.email} is disabled and signed out.`))}
                  />
                )}
                <button type="button" className={`${btnCls} hover:text-ele-danger`} disabled={busy !== null} onClick={() => setRemoving(u)}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {archived ? (
        <p className="text-sm text-ele-body">Portal sign-in is refused while archived. Restore them to add or use a login.</p>
      ) : (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) void add();
          }}
        >
          <label className="text-xs text-ele-body">
            Email
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-64 max-w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
          </label>
          <label className="text-xs text-ele-body">
            Name (optional)
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 block w-48 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
          </label>
          <button type="submit" className={btnCls} disabled={busy !== null || !email.trim()}>
            {busy === "add" ? "Adding..." : "Add login"}
          </button>
        </form>
      )}

      <ConfirmDeleteModal
        open={removing !== null}
        title="Remove portal login"
        message={removing ? `Remove the portal login for ${removing.email}? They are signed out at once and the login is deleted. You can add them again later.` : ""}
        confirmLabel="Remove login"
        busy={busy === "remove"}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const target = removing;
          if (!target) return;
          void run("remove", async () => {
            await api.subcontractorPortalAdmin.remove(subId, target.id);
            setRemoving(null);
            return `The portal login for ${target.email} was removed.`;
          });
        }}
      />
    </section>
  );
}
